import React, { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * ClayGlobe
 * A real 3D sphere with an equirectangular texture mapped onto it.
 * - Auto-rotates on its Y axis
 * - Gently floats up and down
 * - Users can drag (mouse or touch) to spin it manually
 * - Releases resume the auto-rotation with inertia
 */
const ClayGlobe = ({ textureUrl, heightUrl }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ------- Renderer -------
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    // ------- Scene / camera -------
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    camera.position.set(0, 0, 5.2);

    // ------- Lights (clay feel) -------
    scene.add(new THREE.AmbientLight(0xffffff, 0.75));

    const keyLight = new THREE.DirectionalLight(0xffffff, 0.9);
    keyLight.position.set(3, 4, 5);
    scene.add(keyLight);

    const rimLight = new THREE.DirectionalLight(0xffe1c2, 0.35);
    rimLight.position.set(-4, -2, -3);
    scene.add(rimLight);

    // ------- Sphere + texture -------
    const loader = new THREE.TextureLoader();
    const colorTexture = loader.load(textureUrl, (t) => {
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = renderer.capabilities.getMaxAnisotropy();
    });

    // Height map: bright = raised, dark = recessed.
    // Used as both a displacement map (real geometry deformation) and a bump
    // map (shading detail) so letters and continents look physically embossed.
    const heightTexture = heightUrl
      ? loader.load(heightUrl, (t) => {
          t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        })
      : null;

    // Higher segment count so the displacement has fidelity around letterforms.
    const geometry = new THREE.SphereGeometry(1.15, 256, 256);
    const material = new THREE.MeshStandardMaterial({
      map: colorTexture,
      bumpMap: heightTexture || undefined,
      bumpScale: 0.022,
      displacementMap: heightTexture || undefined,
      displacementScale: heightTexture ? 0.03 : 0,
      displacementBias: heightTexture ? -0.008 : 0,
      roughness: 0.82,
      metalness: 0.04,
    });
    const globe = new THREE.Mesh(geometry, material);
    // Slight tilt so it reads as 3D right away
    globe.rotation.x = -0.25;
    scene.add(globe);

    // ------- Interaction state -------
    const state = {
      isDragging: false,
      lastX: 0,
      lastY: 0,
      velX: 0.006, // idle auto-spin speed
      velY: 0.0,
      floatT: 0,
    };

    const onPointerDown = (e) => {
      state.isDragging = true;
      state.lastX = e.clientX;
      state.lastY = e.clientY;
      renderer.domElement.setPointerCapture?.(e.pointerId);
      renderer.domElement.style.cursor = "grabbing";
    };

    const onPointerMove = (e) => {
      if (!state.isDragging) return;
      const dx = e.clientX - state.lastX;
      const dy = e.clientY - state.lastY;
      state.lastX = e.clientX;
      state.lastY = e.clientY;
      // Convert pixel deltas into rotation velocity
      state.velX = dx * 0.005;
      state.velY = dy * 0.005;
      globe.rotation.y += state.velX;
      globe.rotation.x = THREE.MathUtils.clamp(
        globe.rotation.x + state.velY,
        -1.1,
        1.1
      );
    };

    const onPointerUp = () => {
      state.isDragging = false;
      renderer.domElement.style.cursor = "grab";
    };

    renderer.domElement.style.cursor = "grab";
    renderer.domElement.style.touchAction = "none";
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);

    // ------- Sizing -------
    const resize = () => {
      const rect = mount.getBoundingClientRect();
      const size = Math.min(rect.width, rect.height);
      renderer.setSize(size, size, false);
      camera.aspect = 1;
      camera.updateProjectionMatrix();
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(mount);

    // ------- Animation loop -------
    let raf = 0;
    const clock = new THREE.Clock();
    const IDLE_VEL = 0.006;

    const animate = () => {
      const dt = clock.getDelta();

      if (!state.isDragging) {
        // Ease horizontal velocity back toward idle spin
        state.velX += (IDLE_VEL - state.velX) * 0.02;
        state.velY *= 0.9;
        globe.rotation.y += state.velX;
        globe.rotation.x += state.velY;
        globe.rotation.x = THREE.MathUtils.clamp(globe.rotation.x, -1.1, 1.1);
        // Ease X back to gentle tilt
        globe.rotation.x += (-0.25 - globe.rotation.x) * 0.01;
      }

      // Gentle vertical float
      state.floatT += dt;
      globe.position.y = Math.sin(state.floatT * 1.15) * 0.12;

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    // ------- Cleanup -------
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      geometry.dispose();
      material.dispose();
      colorTexture.dispose();
      if (heightTexture) heightTexture.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [textureUrl, heightUrl]);

  return <div ref={mountRef} className="clay-globe" aria-label="Interactive 3D globe" />;
};

export default ClayGlobe;
